<script setup lang="ts">
import { computed, provide, ref, shallowReactive } from 'vue'
import ImagePreview from './ImagePreview.vue'
import { imageGroupKey, type PreviewImage, type ImagePreviewOptions } from './types'

const props = withDefaults(defineProps<{ preview?: boolean | ImagePreviewOptions }>(), { preview: true })
const emit = defineEmits<{ change: [current: number, previous: number] }>()
const entries = shallowReactive(new Map<string, () => PreviewImage>())
const options = computed(() => typeof props.preview === 'object' ? props.preview : {})
const innerOpen = ref(false)
const innerCurrent = ref(0)
const enabled = computed(() => props.preview !== false)
const open = computed(() => enabled.value && (options.value.visible ?? innerOpen.value))
const images = computed(() => [...entries.values()].map(getImage => getImage()))
const current = computed(() => options.value.current ?? innerCurrent.value)
function setOpen(value: boolean) { const previous = open.value; innerOpen.value = value; if (previous !== value) options.value.onVisibleChange?.(value, previous) }
function changeCurrent(value: number) { const previous = current.value; innerCurrent.value = value; if (value !== previous) emit('change', value, previous) }
provide(imageGroupKey, {
  enabled,
  register(id, image) { entries.set(id, image); return () => { entries.delete(id); if (!entries.size) setOpen(false) } },
  open(id) { if (!enabled.value) return; const index = [...entries.keys()].indexOf(id); if (index < 0) return; changeCurrent(index); setOpen(true) }
})
</script>

<template>
  <slot />
  <ImagePreview :open="open" :images="images" :current="current" :get-container="options.getContainer" @update:open="setOpen" @update:current="changeCurrent" />
</template>
